import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  TextField,
  DataTable,
  Spinner,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface BackorderItem {
  product_id: number;
  sku: string;
  title: string;
  vendor: string | null;
  backordered_qty: number;
  order_count: number;
  order_numbers: string;
  oldest_order: string;
  available_stock: number;
}

interface BackorderSummary {
  total_products: number;
  total_units: number;
  total_orders: number;
}

export default function BackorderReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [data, setData] = useState<BackorderItem[]>([]);
  const [summary, setSummary] = useState<BackorderSummary | null>(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const loadReport = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    const result = await fetchApi<{
      success?: boolean;
      error?: string;
      data: BackorderItem[];
      summary: BackorderSummary;
    }>(`/reports/backorder?${params.toString()}`);

    if (result) {
      // Check for API-level errors (returned as 200 with success: false)
      if (result.success === false) {
        console.error('[BackorderReport] API error:', result.error);
        return;
      }
      setData(result.data || []);
      setSummary(result.summary);
    }
  }, [fetchApi, search]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; download_url: string; row_count: number }>('/reports/backorder/export', {
        method: 'POST',
      });

      if (result?.success && result.download_url) {
        window.open(`${import.meta.env.VITE_API_URL}${result.download_url}`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const tableRows = data.map((item) => [
    <Button variant="plain" onClick={() => navigate(`/products/${item.product_id}`)}>
      {item.sku || '-'}
    </Button>,
    <BlockStack gap="050">
      <Text as="span" variant="bodySm" fontWeight="semibold">{item.title}</Text>
      {item.vendor && <Text as="span" variant="bodySm" tone="subdued">{item.vendor}</Text>}
    </BlockStack>,
    <Badge tone="critical">{String(item.backordered_qty)}</Badge>,
    String(item.available_stock),
    String(item.order_count),
    <Text as="span" variant="bodySm" tone="subdued">
      {item.order_numbers?.split(',').slice(0, 3).join(', ')}
      {item.order_numbers?.split(',').length > 3 && '...'}
    </Text>,
    formatDate(item.oldest_order),
  ]);

  return (
    <Page
      title="Backorder Report"
      subtitle="Products with backordered quantities across orders"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: data.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Products on Backorder</Text>
                  <Text as="p" variant="heading2xl">{summary.total_products}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Total Units Backordered</Text>
                  <Text as="p" variant="heading2xl">{summary.total_units}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Affected Orders</Text>
                  <Text as="p" variant="heading2xl">{summary.total_orders}</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start">
              <div style={{ width: '300px' }}>
                <TextField
                  label="Search"
                  labelHidden
                  value={search}
                  onChange={setSearch}
                  autoComplete="off"
                  placeholder="Search SKU or product title..."
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </div>
              <Button onClick={loadReport}>Refresh</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Data Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Backordered Products ({data.length})
                </Text>
              </InlineStack>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : data.length === 0 ? (
                <Banner tone="success">
                  No backordered products found. All orders can be fulfilled with current stock.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                  headings={['SKU', 'Product', 'Backordered', 'Available', 'Orders', 'Order Numbers', 'Oldest Order']}
                  rows={tableRows}
                  sortable={[false, false, true, true, true, false, true]}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
